import { useState, useEffect } from 'react';

import Icons from 'assets/icons';

const ROTATION_INTERVAL_MS = 6000;

interface Goal {
  summary?: string;
  [key: string]: unknown;
}

interface GoalsCaruselProps {
  goals: Goal[];
}

const GoalsCarusel = ({ goals }: GoalsCaruselProps): React.JSX.Element => {
  const [currentGoalIndex, setCurrentGoalIndex] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(false);

  const currentGoal = goals[currentGoalIndex];

  const handleNext = (): void => {
    setIsPaused(true);
    setCurrentGoalIndex((prevIndex) => (prevIndex + 1) % goals.length);
  };

  const handlePrev = (): void => {
    setIsPaused(true);
    setCurrentGoalIndex(
      (prevIndex) => (prevIndex - 1 + goals.length) % goals.length,
    );
  };

  const isSingleGoal = goals.length === 1;

  useEffect(() => {
    if (isSingleGoal || isPaused) return undefined;

    const interval = setInterval(() => {
      setCurrentGoalIndex((prevIndex) => (prevIndex + 1) % goals.length);
    }, ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [goals.length, isPaused, isSingleGoal]);

  return (
    <div
      className="flex-1 bg-navy-50 p-4 rounded-lg flex items-center justify-between"
      aria-roledescription="carousel"
      aria-label="Goals"
    >
      {!isSingleGoal && (
        <button
          onClick={handlePrev}
          className="text-navy-300 hover:text-navy-700 transition"
          aria-label="Previous goal"
        >
          <Icons.Arrow className="h-5 w-5 rotate-180" aria-hidden="true" />
        </button>
      )}

      <div className="flex-1 flex flex-col justify-center items-center text-gray-700">
        <p
          className="font-semibold text-center text-lg mb-2 px-1"
          aria-live="polite"
          aria-atomic="true"
        >
          {currentGoal.summary}
        </p>
        {!isSingleGoal && (
          <button
            onClick={() => setIsPaused((prev) => !prev)}
            className="text-xs text-navy-500 hover:text-navy-700 transition mt-1"
            aria-label={isPaused ? 'Play auto-rotation' : 'Pause auto-rotation'}
          >
            {isPaused ? '▶ Play' : '⏸ Pause'}
          </button>
        )}
      </div>

      {!isSingleGoal && (
        <button
          onClick={handleNext}
          className="text-navy-300 hover:text-navy-700 transition"
          aria-label="Next goal"
        >
          <Icons.Arrow className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default GoalsCarusel;
